import {
	PARTNER_BRAND_ACCOUNTS_FETCHED,
	PARTNER_BRAND_CREATED,
	PARTNER_BRAND_FETCHED,
	PARTNER_BRAND_PAYMENTS_STATUS_FETCHED,
	PARTNER_BRAND_UPDATED,
	PARTNER_BRANDS_FETCHED,
	QUOTE_EMAIL_STYLES_FETCHED
} from "./actionTypes";
import {Action} from "redux-actions";
import PartnerBrand, {IPartnerBrandPaymentsStatus, PartnerBrandSearch} from "../../../model/partnerBrand/PartnerBrand";
import {OnFailure, OnFailureData, OnSuccess, OnSuccessData} from "../actionHelpers";
import {Dispatch} from "react-redux";
import {api} from "../../../api/api";
import {none, option, Option} from "ts-option";
import Address from "../../../model/address/Address";
import SiteData from "../../../model/site/SiteData";
import {siteCreated} from "../site/site";
import {ImportResult} from "../../../model/importResult/ImportResult";
import PartnerBrandPoints from "../../../model/partnerBrand/PartnerBrandPoints";
import {notifyBugsnagAndShowToast} from "../../../utils/Utils";
import PartnerBrandRewardsData from "../../../model/partnerBrand/PartnerBrandRewardsData";
import Account from "../../../model/account/Account";
import QuoteEmailStyle from "../../../model/partnerBrand/QuoteEmailStyle";
import _ = require("underscore");

/**
 * Partner brands fetched successful action.
 * @param partnerBrands {PartnerBrand[]}
 * @returns {Action<PARTNER_BRANDS_FETCHED>}
 */
function partnerBrandsFetched(partnerBrands: Array<PartnerBrandSearch>): Action<PARTNER_BRANDS_FETCHED> {
	return {type: PARTNER_BRANDS_FETCHED, payload: {partnerBrands}};
}

/**
 * Brand created successful action.
 * @param {PartnerBrand} partnerBrand
 * @returns {Action<PARTNER_BRAND_CREATED>}
 */
function partnerBrandCreated(partnerBrand: PartnerBrand): Action<PARTNER_BRAND_CREATED> {
	return {type: PARTNER_BRAND_CREATED, payload: {partnerBrand}};
}

/**
 * Brand updated successful action.
 * @param {PartnerBrand} partnerBrand
 * @returns {Action<PARTNER_BRAND_UPDATED>}
 */
function partnerBrandUpdated(partnerBrand: PartnerBrand): Action<PARTNER_BRAND_UPDATED> {
	return {type: PARTNER_BRAND_UPDATED, payload: {partnerBrand}};
}

/**
 * Partner brand accounts fetched successful action.
 * @returns {Action<PARTNER_BRANDS_FETCHED>}
 * @param accounts
 * @param partnerBrandId
 */
function partnerBrandAccountsFetched(accounts: Array<Account>, partnerBrandId: string): Action<PARTNER_BRAND_ACCOUNTS_FETCHED> {
	return {type: PARTNER_BRAND_ACCOUNTS_FETCHED, payload: {accounts, partnerBrandId}};
}

/**
 * Partner brand payments status fetched successful action.
 * @param partnerBrandId
 * @param paymentStatus
 * @returns {Action<PARTNER_BRAND_PAYMENTS_STATUS_FETCHED>}
 */
function partnerBrandPaymentsStatusFetched(partnerBrandId: string,
	paymentStatus: IPartnerBrandPaymentsStatus): Action<PARTNER_BRAND_PAYMENTS_STATUS_FETCHED> {
	return {type: PARTNER_BRAND_PAYMENTS_STATUS_FETCHED, payload: {partnerBrandId, paymentStatus}};
}

/**
 * Partner brand fetched successful action.
 * @param partnerBrand {PartnerBrand}
 * @returns {Action<PARTNER_BRAND_FETCHED>}
 */
function partnerBrandFetched(partnerBrand: PartnerBrand): Action<PARTNER_BRAND_FETCHED> {
	return {type: PARTNER_BRAND_FETCHED, payload: {partnerBrand}};
}

/**
 * Fetch partner brands from server.
 * @param ids
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @return {(dispatch: Dispatch<any>) => void}
 */
export function fetchPartnerBrands(ids: string[], onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		const uniqArray = _.uniq(ids);
		if (uniqArray.length) {
			const pageSize = 999
			api.instance.partnerBrandApi.fetchPartnerBrands(uniqArray, 1, pageSize).then(resp => {
				const partnerBrands = resp.items.map(PartnerBrandSearch.fromJson)
				const batchCount = Math.ceil(resp.totalItems / pageSize);
				if (batchCount > 1) {
					const promises = []
					for (let i = 2; i <= batchCount; i++) {
						promises.push(api.instance.partnerBrandApi.fetchPartnerBrands(uniqArray, i, pageSize))
					}
					Promise.all(promises).then(resp => {
						resp.forEach(r => partnerBrands.push(...r.items.map(PartnerBrandSearch.fromJson)));
						dispatch(partnerBrandsFetched(partnerBrands));
						onSuccess()
					}).catch(onFailure)
				}
				else {
					dispatch(partnerBrandsFetched(partnerBrands));
					onSuccess();
				}

			}).catch(onFailure);
		}
		else {
			onSuccess()
		}
	};
}

/**
 * Create partner brand action.
 * Upload partner brand logo if given before creating.
 * @param {PartnerBrand} partnerBrand created.
 * @param {Option<File>} file optional logo of the brand to upload
 * @param {Option<Address>} address optional brand address.
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @return {(dispatch: Dispatch<any>) => void}
 */
export function createPartnerBrand(partnerBrand: PartnerBrand,
	file: Option<File>,
	address: Option<Address>,
	onSuccess: OnSuccessData<PartnerBrand>,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		const create = (data: PartnerBrand) => {
			return api.instance.partnerBrandApi.createPartnerBrand(data).then(createdBrand => {
				dispatch(partnerBrandCreated(createdBrand));
				onSuccess(createdBrand);
			}).catch(onFailure);
		};
		const createWithSite = (data: PartnerBrand) => {
			if (address.isDefined) {
				return api.instance.siteApi.createSite(new SiteData(address.get)).then(site => {
					dispatch(siteCreated(site));
					return create(data.copy({siteId: option(site.id)}))
				}).catch(onFailure)
			}
			else {
				return create(data);
			}
		};
		if (file.isDefined) {
			return api.instance.attachmentApi.uploadFile(file.get).then(id => {
				return createWithSite(partnerBrand.copy({logoId: option(id)}));
			}).catch(onFailure)
		}
		else {
			return createWithSite(partnerBrand);
		}
	}
}

/**
 * Update partner brand action.
 * Upload partner brand logo if given before updating.
 * @param partnerBrandId {string}
 * @param {PartnerBrand} partnerBrand partner brand created data.
 * @param {Option<File>} file optional logo of the brand to upload
 * @param {Option<Address>} address optional brand address.
 * @param shouldRemoveFileOnUpdate
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @return {(dispatch: Dispatch<any>) => void}
 */
export function updatePartnerBrand(partnerBrandId: string,
	partnerBrand: PartnerBrand,
	file: Option<File>,
	address: Option<Address>,
	shouldRemoveFileOnUpdate: boolean,
	onSuccess: OnSuccessData<PartnerBrand>,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		const update = (data: PartnerBrand) => {
			api.instance.partnerBrandApi.updatePartnerBrand(partnerBrandId, data).then(updatedBrand => {
				dispatch(partnerBrandUpdated(updatedBrand));
				onSuccess(updatedBrand);
			}).catch(onFailure);
		};

		const updateWithSite = (data: PartnerBrand) => {
			if (address.isDefined) {
				return api.instance.siteApi.createSite(new SiteData(address.get)).then(site => {
					dispatch(siteCreated(site));
					return update(data.copy({siteId: option(site.id)}))
				}).catch(onFailure)
			}
			else {
				return update(data);
			}
		};
		if (file.isDefined) {
			return api.instance.attachmentApi.uploadFile(file.get).then(id => {
				return updateWithSite(partnerBrand.copy({logoId: option(id)}));
			}).catch(onFailure)
		}
		else {
			return updateWithSite(partnerBrand.copy({logoId: shouldRemoveFileOnUpdate ? none : partnerBrand.logoId}));
		}
	}
}

/**
 * Add partner brand rewords points
 * Requests called synchronously due to sorting errors (same time createdAt)
 * @param {PartnerBrandPoints[]} arr
 * @param {string} pbId
 */
export function addPartnerBrandRewardsPoints(arr: PartnerBrandPoints[], pbId: string) {
	let index = 0;

	const request: () => void = () => {
		return api.instance.partnerBrandApi.addPoint(pbId, arr[index])
			.then(callNextOrReturn)
			.catch((err) => {
				notifyBugsnagAndShowToast(err, "Reward points add");
				callNextOrReturn()
			})
	};

	const callNextOrReturn = () => {
		index++;
		if (index >= arr.length) {
			return
		}
		return request();
	};

	return request();
}

/**
 * Update partner brand sign up date and reward status.
 * @returns {Promise<PartnerBrand | void>}
 * @param partnerBrandId
 * @param rewardsData
 */
export function updateRewardsData(partnerBrandId: string, rewardsData: PartnerBrandRewardsData) {
	return api.instance.partnerBrandApi.updateRewardsData(partnerBrandId, rewardsData)
		.catch((err) => notifyBugsnagAndShowToast(err, "Update rewards data"));
}

/**
 * Fetch partner brand accounts from server.
 * @param partnerBrandId
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @return {(dispatch: Dispatch<any>) => void}
 */
export function fetchPartnerBrandAccounts(partnerBrandId: string, onSuccess: OnSuccessData<Account[]>, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.accountApi.fetchAccounts(partnerBrandId).then(accounts => {
			dispatch(partnerBrandAccountsFetched(accounts, partnerBrandId));
			onSuccess(accounts);
		}).catch(onFailure);
	};
}

/**
 * Quote email templates styles fetched successful action.
 * @param quoteEmailStyles {QuoteEmailStyle[]}
 * @returns {Action<QUOTE_EMAIL_STYLES_FETCHED>}
 */
function quoteEmailStylesFetched(quoteEmailStyles: Array<QuoteEmailStyle>): Action<QUOTE_EMAIL_STYLES_FETCHED> {
	return {type: QUOTE_EMAIL_STYLES_FETCHED, payload: {quoteEmailStyles}};
}

/**
 * Fetch quote email templates styles list from server.
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @return {(dispatch: Dispatch<any>) => void}
 */
export function fetchQuoteEmailStyles(onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.partnerBrandApi.fetchQuoteEmailStyles().then(quoteEmailStyles => {
			dispatch(quoteEmailStylesFetched(quoteEmailStyles));
			onSuccess();
		}).catch(onFailure);
	};
}

export function uploadPartnerBrands(file: File, onSuccess: OnSuccess, onFailure: OnFailureData<Option<ImportResult>>) {
	return function() {
		api.instance.partnerBrandApi.uploadPartnerBrands(file)
			.then(onSuccess)
			.catch(error => {
				if (error.response) {
					onFailure(option(ImportResult.fromJson(error.response.data)))
				}
				else {
					onFailure(none);
				}
			});
	};
}

export function fetchPBrandPaymentsStatus(partnerBrandId: string, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.partnerBrandApi.checkPartnerBrandPaymentsStatus(partnerBrandId)
			.then(status => {
				dispatch(partnerBrandPaymentsStatusFetched(partnerBrandId, status))
			})
			.catch(onFailure)
	}
}

/**
 * Fetch partner brand from server.
 * @param id
 * @param {OnFailure} onFailure
 * @return {(dispatch: Dispatch<any>) => void}
 */
export function fetchPartnerBrand(id: string, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.partnerBrandApi.fetchPartnerBrand(id).then(partnerBrand => {
			dispatch(partnerBrandFetched(partnerBrand));
		}).catch(onFailure);
	};
}

/**
 * Fetch partner brand from server again, e.g. after it was saved, and tell the caller when it is in the store.
 * @param id
 * @return {(dispatch: Dispatch<any>) => Promise<PartnerBrand>}
 */
export function refreshPartnerBrand(id: string) {
	return function(dispatch: Dispatch<any>): Promise<PartnerBrand> {
		return api.instance.partnerBrandApi.fetchPartnerBrand(id).then(partnerBrand => {
			dispatch(partnerBrandFetched(partnerBrand));
			return partnerBrand;
		});
	};
}

export function savePartnerBrandSearch(pb: PartnerBrandSearch) {
	return function(dispatch: Dispatch<any>) {
		dispatch(partnerBrandsFetched([pb]))
	}
}